import { useContext } from "react";

import { getSolutionBySlug, getSolutions } from "../../lib/solutions";
import { BookTourFormContext } from "../../hooks/useBookTourForm";
import { getSEOPageProps } from "../../lib/seo";

import Button from "../../components/button";
import Hero from "../../components/hero";
import MeetOurClients from "../../components/meet-our-clients";
import OptionListItem from "../../components/option-list-item";
import OtherSolutions from "../../components/other-solutions";
import SocialMembership from "../../components/social-membership";
import VideoSection from "../../components/video-section";
import SeoMeta from "../../components/seo-meta";

import styles from "./solution.module.scss";
import BookingForm from "../../components/booking-form";
import config from "../../config";

export default function SolutionDetails({ solution, seo }) {
  const { setBookTourFormOpen } = useContext(BookTourFormContext);

  return (
    <div className="page">
      <div className="container">
        <SeoMeta {...seo} />
        <Hero
          title={solution.name}
          subtitle={solution.subtitle}
          description={solution.description}
          image={solution.image}
          cta={
            <div className={styles.Buttons}>
              <div>
                <Button
                  outline
                  small
                  rounded
                  onClick={() => setBookTourFormOpen(true)}
                >
                  Book a Tour
                </Button>
              </div>
              <div>
                {solution.attachment && (
                  <Button
                    outline
                    small
                    rounded
                    onClick={() => {
                      window.open(solution.attachment.url, "_blank");
                    }}
                  >
                    {solution.attachmentCtaLabel || "Download"}
                  </Button>
                )}
              </div>
            </div>
          }
          lightWeightFont
        />
      </div>

      <VideoSection
        key={solution.slug}
        title={solution.videoTitle}
        description={solution.videoDescription}
        video={solution.video}
      />

      <div className="container">
        {solution.optionSectionsCollection.items.map((optionSection) => (
          <div key={optionSection.title} className={styles.OptionList}>
            <div className={styles.OptionDescription}>
              <div className={styles.OptionText}>
                <h3>{optionSection.title}</h3>
                <p>{optionSection.description}</p>
              </div>
            </div>
            <div className={styles.SolutionOptionList}>
              {optionSection.optionsCollection.items.map((option) => (
                <OptionListItem key={option.name} option={option} />
              ))}
            </div>
          </div>
        ))}

        <div className={styles.OtherSolutionsWrapper}>
          <OtherSolutions
            solutions={solution.otherSolutionsCollection.items}
          ></OtherSolutions>
        </div>
      </div>

      <SocialMembership
        title="Refer a Friend and Make Bank"
        subtitle="Referral"
        description="Refer like-minded companies, independent professionals, and social-event seekers to East Room. When they sign up or use our services, you’ll earn credits toward your membership."
        url="/referral-program"
        image="/images/member.gif"
        buttonText="Refer Here"
      />
      <div className="container">
        <MeetOurClients />
      </div>

      <div className={styles.MobileBookingForm}>
        <BookingForm />
      </div>
    </div>
  );
}

// Three solutions, all in the primary nav — cheap to prerender in full.
export async function getStaticPaths() {
  const solutions = await getSolutions();

  return {
    paths: (solutions || []).map((solution) => ({ params: { slug: solution.slug } })),
    fallback: "blocking"
  };
}

export async function getStaticProps({ params }) {
  try {
    const [solution, seo] = await Promise.all([
      getSolutionBySlug(params.slug),
      getSEOPageProps({ resolvedUrl: `/solutions/${params.slug}` })
    ]);

    if (!solution) {
      return { notFound: true, revalidate: config.NOT_FOUND_TTL };
    }

    return {
      props: { solution, seo },
      revalidate: config.ISR_TIMING
    };
  } catch (e) {
    console.log(e.message);
    return { notFound: true, revalidate: config.NOT_FOUND_TTL };
  }
}

